import { Link } from 'react-router-dom';
import { ArrowRight, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const ReviewsPage = () => {
  const reviews = [
    {
      name: 'Michael Torres',
      role: 'Marketing Director, TechStart Inc.',
      text: 'Charles Print delivered our business cards in record time. The quality exceeded our expectations - crisp colors, premium paper stock, and spot-on alignment. We\'ve made them our go-to print shop for all marketing materials.',
      rating: 5,
      date: 'November 2024',
    },
    {
      name: 'Sarah Chen',
      role: 'Owner, Chen\'s Bakery',
      text: 'Best print shop in Manhattan! I needed menus printed urgently for our grand opening, and they delivered same-day with perfect results. Professional service and competitive prices. Highly recommend!',
      rating: 5,
      date: 'October 2024',
    },
    {
      name: 'David Miller',
      role: 'Event Coordinator, NYC Events Co.',
      text: 'We use Charles Print for all our event materials - banners, flyers, programs, you name it. They never disappoint. True NYC quality with attention to every detail.',
      rating: 5,
      date: 'October 2024',
    },
    {
      name: 'Jennifer Williams',
      role: 'Creative Director, Studio 54 Design',
      text: 'As a designer, I\'m very particular about print quality. Charles Print nails it every time. Their color accuracy is impressive, and they understand the importance of getting details right.',
      rating: 5,
      date: 'September 2024',
    },
    {
      name: 'Robert Kim',
      role: 'Attorney, Kim & Associates',
      text: 'Professional letterheads, envelopes, and business cards - all delivered with impeccable quality. The team was helpful in selecting the right paper stock for our firm\'s image.',
      rating: 5,
      date: 'September 2024',
    },
    {
      name: 'Amanda Foster',
      role: 'Marketing Manager, Retail Solutions',
      text: 'We ordered 5,000 promotional flyers for our store opening. Charles Print offered great bulk pricing and delivered ahead of schedule. The print quality was excellent.',
      rating: 5,
      date: 'August 2024',
    },
    {
      name: 'James Patterson',
      role: 'Photographer',
      text: 'I needed portfolio prints on premium photo paper. The results were stunning - true to color and beautifully finished. Will definitely be back for my next exhibition materials.',
      rating: 5,
      date: 'August 2024',
    },
    {
      name: 'Lisa Rodriguez',
      role: 'HR Director, Finance Corp',
      text: 'Charles Print handles all our corporate printing needs - from annual reports to business cards for new hires. Reliable, consistent quality, and easy to work with.',
      rating: 5,
      date: 'July 2024',
    },
    {
      name: 'Thomas Wright',
      role: 'Restaurant Owner',
      text: 'They printed our new menus and promotional materials beautifully. Great attention to detail and the staff was very helpful with paper selection. Prices are fair for the quality.',
      rating: 5,
      date: 'July 2024',
    },
  ];

  const avgRating = (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <div data-testid="reviews-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-white paper-texture" data-testid="reviews-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={`hero-star-${i}`} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter uppercase">
            {avgRating} Star Rating
          </h1>
          <p className="mt-4 text-xl text-zinc-600">
            Based on {reviews.length}+ Google Reviews
          </p>
          <div className="mt-8 inline-flex items-center gap-2 bg-zinc-100 px-6 py-3 rounded-sm">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-medium">Google Reviews</span>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="reviews-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div
                key={index}
                className="review-card bg-white border border-zinc-200 p-8 hover:border-[#3EACDB]/50 transition-colors"
                data-testid={`review-${index}`}
              >
                <Quote className="w-8 h-8 text-[#3EACDB]/20 mb-4" />
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-zinc-700 leading-relaxed mb-6">"{review.text}"</p>
                <div className="border-t border-zinc-100 pt-4">
                  <p className="font-semibold text-zinc-900">{review.name}</p>
                  <p className="text-sm text-zinc-500">{review.role}</p>
                  <p className="text-xs text-zinc-400 mt-1">{review.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-white" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Why Customers Trust Us</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '5.0', label: 'Google Rating' },
              { value: '100%', label: 'Satisfaction' },
              { value: '24hr', label: 'Rush Available' },
              { value: '15+', label: 'Years Experience' },
            ].map((item, index) => (
              <div key={item.label} className="text-center" data-testid={`badge-${index}`}>
                <p className="font-heading text-3xl md:text-4xl font-black text-[#3EACDB]">{item.value}</p>
                <p className="mt-2 text-zinc-600 text-sm uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-zinc-900 text-white" data-testid="reviews-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold">
            Ready to Experience the Difference?
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Join hundreds of satisfied NYC businesses. Get your free quote today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="reviews-cta-quote">
              <Button className="h-12 px-8 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                Make a Rush Order
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="reviews-cta-contact">
              <Button variant="outline" className="h-12 px-8 text-sm font-bold uppercase tracking-wider rounded-sm border-2 border-white text-white hover:bg-white hover:text-zinc-900">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
